import React, { useMemo, useEffect, useState } from 'react'
import Statistic from '@byma/shared/components/Statistic'
import Breadcrumbs from '@byma/shared/components/Breadcrumbs'
import useToastContext from "@byma/shared/hooks/useToastContext";
import dayjs from 'dayjs'
import { Virtuoso } from 'react-virtuoso'
import Link from 'next/link'
import Router, { useRouter } from 'next/router'
import Layout from 'components/Layout'
import useAuth from '@byma/shared/hooks/useAuth'
import { FiArrowLeftCircle, FiEye, FiEyeOff } from 'react-icons/fi'
import useSWR from 'swr'
import fetcher from '@byma/shared/helpers/fetcher'
import Loader from '@byma/shared/components/Loader'
import Input from '@byma/shared/components/Input'
import useFilters from '@byma/shared/hooks/useFilters'
import SearchInput from '@byma/shared/components/SearchInput'
import useSWRInfinite from 'swr/infinite'

function toQueryString(obj, forceKey=null) {
    return Object.keys(obj).map(function (key) {
        if (typeof obj[key] === 'object') {
            return toQueryString(obj[key], key)
        }
        return (forceKey || key) + '=' + obj[key]
    }).join('&');
}

function EventItem({ event, active = false }) {
    return (
        <div className={`flex justify-between items-center cursor-pointer box box-hover ${active && 'box-active'}`}>
            <div className="flex items-center justify-center">
                {event?.pictures?.photos[0] ? (
                    <img src={event.pictures.photos[0]} alt={event.name} className="rounded-full w-12 h-12 mr-3 object-cover" />
                ) : (
                    <div className="w-12 h-12 rounded-full bg-gray-400 overflow-hidden mr-3" />
                )}
                <div className="">
                    <p className="text-md font-bold">{event.name}</p>
                    <p className="text-sm">{dayjs(event.date).format('DD/MM/YYYY HH:mm')}</p>
                </div>
            </div>
            {event.listed ? (
                <span class="text-sm font-medium bg-green-200 py-1 px-2 rounded text-green-800 font-bold align-middle flex justify-center items-center gap-1">
                    <FiEye />
                    P&uacute;blico
                </span>
            ) : (
                <span class="text-sm font-medium bg-yellow-200 py-1 px-2 rounded text-yellow-800 font-bold align-middle flex justify-center items-center gap-1">
                    <FiEyeOff />
                    Secreto
                </span>
            )}
        </div>
    )
}
export default function Events() {
    const session = useAuth()
    const { query, isReady, ...router } = useRouter()
    const { eventId } = query
    const { token, user: currentUser } = session

    const [sortDirection, setSortDirection] = useState(-1)

    //const { data: parties } = useSWR(token ? [`/api/parties/getAll?search=${searchQuery}`, null, token] : null, fetcher)
    const { data: eventTicketTypes } = useSWR(token && eventId ? ['/api/tickettypes/getAll?partyIds=' + eventId, null, token] : null, fetcher)
    const { data: eventPromoters } = useSWR(token && eventId ? ['/api/promoters/getAll?partyIds=' + eventId, null, token] : null, fetcher)
    const { data: eventCoupons } = useSWR(token && eventId ? ['/api/coupons/getAll?partyIds=' + eventId, null, token] : null, fetcher)
    const { data: eventSelected } = useSWR(token && eventId ? ['/api/parties/get?eventId=' + eventId, null, token] : null, fetcher)

    const { originalQueryString, parsedFilters, rawFilters } = useFilters({ ...query, d:'all'}, () => true)

    const breadcrumbPaths = [
        { label: 'Eventos', url: `/events` },
    ]

    function getKey(pageIndex, previousPageData) {
        if (!token) return null
        if (previousPageData?.result?.length === 0) return null

        if (pageIndex === 0) {
            return [`/api/parties/getAll?${toQueryString({...parsedFilters, sort: sortDirection, perPage: 20})}`, null, token]
        }

        const lastResult = previousPageData?.result[previousPageData.result.length - 1]
        if (!lastResult) return null

        return [`/api/parties/getAll?${toQueryString({...parsedFilters, sort: sortDirection, toDate: lastResult.date, perPage: 20})}`, null, token]
    }

    const { data: parties, isValidating, size, setSize } = useSWRInfinite(getKey, fetcher)

    const addToast = useToastContext()
    const inputRef = React.useRef(null)
    function copyURL(e) {
        if (inputRef.current) {
            inputRef.current.select()
            document.execCommand('copy')
            e.target.focus()
            addToast('Copiado!')
        }
    }
    const partyLink = (party) => `https://byma.com.br/event/${party._id}`

    const tableData = useMemo(() => {
        if (!parties) {
            return []
        }

        return parties.reduce((acc, cur) => {
            return [...acc, ...(cur?.result || [])]
        }, [])
    }, [parties])


    const distribution = useMemo(() => {
        if (!eventTicketTypes || !eventTicketTypes?.result) {
            return {
                percentage: 0,
                totalSold: 0,
                totalTarget: 0,
            }
        }
        const totalSold = eventTicketTypes.result.reduce((acc, cur) => acc + cur.ticketsSold, 0) || 0
        const totalTarget = eventTicketTypes.result.reduce((acc, cur) => acc + cur.target, 0) || 0

        return {
            percentage: Math.round(totalSold / totalTarget * 100) || 0,
            totalSold,
            totalTarget,
        }
    }, [eventTicketTypes])

    function rowRenderer(index, data) {
        const event = tableData[index] || {}
        return (
            <div
                key={index}
                className="px-3"
            >
                <Link href={`/events/${event._id}?${originalQueryString}`}>
                    <a>
                        <EventItem {...{ event }} active={eventSelected && eventSelected._id === event._id} />
                    </a>
                </Link>
            </div>
        )
    }
    return (
        <Layout {...{ session }}>
            <div className="flex h-full">
                <div className={`flex flex-col w-full space-y-3`}>
                    <div className="flex items-center space-x-3 p-3 shadow w-full overflow-x-auto">
                        <SearchInput currentQuery={rawFilters.search} />
                        <Link href={`/events/new`}>
                            <a>
                                <button className="btn-filters">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                                    </svg>
                                    <p>Novo evento</p>
                                </button>
                            </a>
                        </Link>
                    </div>
                    <div className="px-3 flex items-center justify-between">
                        <Breadcrumbs paths={breadcrumbPaths} />
                        <div className="flex items-center space-x-2 justify-center">
                            {parseInt(sortDirection, 10) === -1 ? (
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                                    <path fillRule="evenodd" d="M2 3.75A.75.75 0 0 1 2.75 3h11.5a.75.75 0 0 1 0 1.5H2.75A.75.75 0 0 1 2 3.75ZM2 7.5a.75.75 0 0 1 .75-.75h7.508a.75.75 0 0 1 0 1.5H2.75A.75.75 0 0 1 2 7.5ZM14 7a.75.75 0 0 1 .75.75v6.59l1.95-2.1a.75.75 0 1 1 1.1 1.02l-3.25 3.5a.75.75 0 0 1-1.1 0l-3.25-3.5a.75.75 0 1 1 1.1-1.02l1.95 2.1V7.75A.75.75 0 0 1 14 7ZM2 11.25a.75.75 0 0 1 .75-.75h4.562a.75.75 0 0 1 0 1.5H2.75a.75.75 0 0 1-.75-.75Z" clipRule="evenodd" />
                                </svg>

                            ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                                    <path fillRule="evenodd" d="M2 3.75A.75.75 0 0 1 2.75 3h11.5a.75.75 0 0 1 0 1.5H2.75A.75.75 0 0 1 2 3.75ZM2 7.5a.75.75 0 0 1 .75-.75h6.365a.75.75 0 0 1 0 1.5H2.75A.75.75 0 0 1 2 7.5ZM14 7a.75.75 0 0 1 .55.24l3.25 3.5a.75.75 0 1 1-1.1 1.02l-1.95-2.1v6.59a.75.75 0 0 1-1.5 0V9.66l-1.95 2.1a.75.75 0 1 1-1.1-1.02l3.25-3.5A.75.75 0 0 1 14 7ZM2 11.25a.75.75 0 0 1 .75-.75H7A.75.75 0 0 1 7 12H2.75a.75.75 0 0 1-.75-.75Z" clipRule="evenodd" />
                                </svg>

                            )}
                            <select onChange={(e) => setSortDirection(e.target.value)} value={sortDirection} className="focus:outline-none border-0 p-0 m-0 shadow-none appearance-none">
                                <option value="1">Data crescente</option>
                                <option value="-1">Data decrescente</option>
                            </select>
                        </div>
                    </div>
                    <Virtuoso
                        data={tableData}
                        endReached={() => setSize(size+1)}
                        overscan={200}
                        itemContent={rowRenderer}
                        components={{
                            Header: () => {
                                return (
                                    <div
                                        className="h-2"
                                    />
                                )
                            },
                            Footer: () => {
                                return (
                                    <div
                                        className="p-3 flex items-center justify-center"
                                    >
                                        { isValidating ? (
                                            <Loader /> 
                                        ): (
                                            <p className="text-sm text-gray-700">
                                                { tableData?.length === 0 ? 'Nenhum resultado encontrado' : 'Fim dos resultados' }
                                            </p>
                                        )}
                                    </div>
                                )
                            }
                        }}
                    />
                </div>
                <div className={`fixed w-full h-full inset-0 transition-opacity duration-100 ${eventId ? 'z-50 opacity-100' : '-z-1 opacity-0'}`} onClick={() => router.push(`/events?${originalQueryString}`)}>
                    <div class="absolute w-full h-full bg-gray-900 opacity-50" />
                    <div class="modal-content p-3 absolute bg-white w-full md:w-11/12 md:max-w-6xl md:mx-auto md:rounded md:shadow-lg z-50 overflow-y-auto max-h-screen" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end">
                            <Link href={`/events?${originalQueryString}`}>
                                <a>
                                    <button>
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </a>
                            </Link>
                        </div>
                        {eventId && !eventSelected && (
                            <Loader />
                        )}
                        {eventId && eventSelected && (
                            <div className="md:grid md:grid-cols-6 md:gap-3">
                                <div className="md:col-span-2">
                                    <div className="mb-6">
                                        <div className="mb-3">
                                            {eventSelected?.result?.pictures?.photos[0] ? (
                                                <img src={eventSelected.result.pictures.photos[0]} alt={eventSelected.result.name} className="rounded h-32 object-cover w-full" />
                                            ) : (
                                                <div className="h-32 bg-gray-400 rounded overflow-hidden" />
                                            )}
                                        </div>
                                        <div className="flex items-center justify-between space-x-3">
                                            <div>
                                                <p className="text-xl font-bold">{eventSelected.result.name}</p>
                                                <p className="text-md">{dayjs(eventSelected.result.date).format('DD/MM/YYYY HH:mm')}</p>
                                            </div>
                                            <a target="_blank" href={partyLink(eventSelected.result)}>
                                                <button className="btn">
                                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                                                        <path fillRule="evenodd" d="M4.25 5.5a.75.75 0 00-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 00.75-.75v-4a.75.75 0 011.5 0v4A2.25 2.25 0 0112.75 17h-8.5A2.25 2.25 0 012 14.75v-8.5A2.25 2.25 0 014.25 4h5a.75.75 0 010 1.5h-5z" clipRule="evenodd" />
                                                        <path fillRule="evenodd" d="M6.194 12.753a.75.75 0 001.06.053L16.5 4.44v2.81a.75.75 0 001.5 0v-4.5a.75.75 0 00-.75-.75h-4.5a.75.75 0 000 1.5h2.553l-9.056 8.194a.75.75 0 00-.053 1.06z" clipRule="evenodd" />
                                                    </svg>

                                                </button>
                                            </a>
                                        </div>
                                    </div>
                                    <Link href={`/teams/${eventSelected.result?.partnerId?._id}`}>
                                        <a>
                                            <div className="box w-full mt-3">
                                                <div className="flex justify-between items-center">
                                                    <div className="text-left min-w-0">
                                                        <p className="text-md font-bold">Equipe</p>
                                                    </div>
                                                    <div className="text-right">
                                                        <p className="text-md">{eventSelected.result?.partnerId?.name}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </a>
                                    </Link>
                                    <div 
                                        className="w-full p-0 box overflow-hidden"
                                    >
                                        <div
                                            className="p-3 grid grid-cols-3 w-full overflow-hidden"
                                            style={{
                                                background: `linear-gradient(to right, #ea560d ${distribution.percentage}%, #f2eae0 ${distribution.percentage}%)`,
                                            }}
                                        >
                                            <div className="flex flex-col items-start justify-center">
                                                <p
                                                    className="text-xs text-left text-black font-bold mb-1"
                                                >
                                                    Vendidos
                                                </p>
                                                <p 
                                                    className="text-xs text-left bg-white px-1 py-1 rounded"  
                                                    title="Somatório de limites dos lotes"
                                                >
                                                {distribution.totalSold}
                                                </p>
                                            </div>
                                            <div className="flex flex-col items-center justify-center">
                                                <p
                                                    className="text-xs text-center text-black font-bold mb-1"
                                                >
                                                    Ocupação
                                                </p>
                                                <p className="bg-white rounded py-1 px-2 text-xs shadow text-center">
                                                    {distribution.percentage}% 
                                                </p>
                                            </div>
                                            <div className="flex flex-col items-end justify-center">
                                                <p
                                                    className="text-xs text-left text-black font-bold mb-1"
                                                >
                                                    Total
                                                </p>
                                                <p 
                                                    className="text-xs text-left bg-white px-1 py-1 rounded"  
                                                    title="Somatório de limites dos lotes"
                                                >
                                                    {distribution.totalTarget}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex space-x-2 my-3 border-b pb-3">
                                        <input className="w-full p-3 shadow rounded" value={partyLink(eventSelected.result)} ref={inputRef} />
                                        <button className="flex gap-1 items-center btn btn-gray" type="button" onClick={copyURL}>
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                <path d="M7 9a2 2 0 012-2h6a2 2 0 012 2v6a2 2 0 01-2 2H9a2 2 0 01-2-2V9z" />
                                                <path d="M5 3a2 2 0 00-2 2v6a2 2 0 002 2V5h8a2 2 0 00-2-2H5z" />
                                            </svg>
                                            Copiar
                                        </button>
                                    </div>
                                    <div className="pb-3">
                                            <div className="flex flex-col space-y-3 items-center">
                                                <Link href={`/report?p=${eventSelected.result?._id}&d=all`}>
                                                    <a className="w-full">
                                                        <button className="btn btn-gray w-full">
                                                            <p>Relatório rápido</p>
                                                        </button>
                                                    </a>
                                                </Link>
                                                <Link href={`/invoices?p=${eventSelected.result?._id}&d=all`}>
                                                    <a className="w-full">
                                                        <button className="btn btn-gray w-full">
                                                            <p>Fechamento rápido</p>
                                                        </button>
                                                    </a>
                                                </Link>
                                            </div>
                                    </div>
                                </div>
                                <div className="col-span-4 flex flex-col space-y-3">
                                    <Link href={`/events/${eventSelected.result._id}/tickettypes`}>
                                        <a>
                                            <div className="flex justify-between items-center rounded hover:bg-gray-100 p-3 cursor-pointer border">
                                                <div>
                                                    <p className="text-lg">Lotes</p>
                                                    <p className="text-sm text-gray-700">Para gerenciar e criar novos lotes de ingressos</p>
                                                </div>
                                                {eventTicketTypes && (
                                                    <div className="bg-gray-200 rounded-full px-3 py-1">
                                                        {eventTicketTypes.count}
                                                    </div>
                                                )}

                                            </div>
                                        </a>
                                    </Link>
                                    <Link href={`/events/${eventSelected.result._id}/promoters`}>
                                        <a>
                                            <div className="flex justify-between items-center rounded hover:bg-gray-100 p-3 cursor-pointer border">
                                                <div>
                                                    <p className="text-lg">Promoters</p>
                                                    <p className="text-sm text-gray-700">Gerenciamento de vendedores e pontos de venda</p>
                                                </div>
                                                {eventPromoters && (
                                                    <div className="bg-gray-200 rounded-full px-3 py-1">
                                                        {eventPromoters.count}
                                                    </div>
                                                )}
                                            </div>
                                        </a>
                                    </Link>
                                    <Link href={`/events/${eventSelected.result._id}/coupons`}>
                                        <a>
                                            <div className="flex justify-between items-center rounded hover:bg-gray-100 p-3 cursor-pointer border">
                                                <div>
                                                    <p className="text-lg">Cupons</p>
                                                    <p className="text-sm text-gray-700">Crie cupons de desconto para lotes secretos</p>
                                                </div>
                                                {eventCoupons && (
                                                    <div className="bg-gray-200 rounded-full px-3 py-1">
                                                        {eventCoupons.count}
                                                    </div>
                                                )}
                                            </div>
                                        </a>
                                    </Link>
                                    <Link href={`/events/${eventId}/page-editor`}> 
                                        <a>
                                            <div className="flex justify-between items-center rounded hover:bg-gray-100 p-3 cursor-pointer border">
                                                <div>
                                                    <p className="text-lg">Apar&ecirc;ncia</p>
                                                    <p className="text-sm text-gray-700">Customize a apar&ecirc;ncia da p&aacute;gina do evento</p>
                                                </div>
                                            </div>
                                        </a>
                                    </Link>
                                    <Link href={`/events/${eventId}/integrations`}> 
                                        <a>
                                            <div className="flex justify-between items-center rounded hover:bg-gray-100 p-3 w-full cursor-pointer border"> 
                                                <div>
                                                    <p className="text-lg text-left">Integra&ccedil;&otilde;es</p>
                                                    <p className="text-sm text-gray-700">Associe pixels de rastreamento e ferramentas de analytics ao evento</p>
                                                </div>
                                            </div>
                                        </a>
                                    </Link>
                                    <Link href={`/events/${eventId}/edit`}> 
                                        <a>
                                            <div className="flex justify-between items-center rounded hover:bg-gray-100 p-3 w-full border"> 
                                                <div>
                                                    <p className="text-lg text-left">Configura&ccedil;&otilde;es</p>
                                                    <p className="text-sm text-gray-700">Outras configura&ccedil;&otilde;es diversas</p>
                                                </div>
                                            </div>
                                        </a>
                                    </Link>
                                    <Link href={`/gatewaySessions?p=${eventSelected.result?._id}&d=all`}>
                                        <a>
                                            <div className="flex justify-between items-center rounded hover:bg-gray-100 p-3 cursor-pointer border">
                                                <div>
                                                    <p className="text-lg">Sessões de portaria</p>
                                                    <p className="text-sm text-gray-700">Administre as sessões de portaria que serão usadas para validação dos ingressos do evento</p>
                                                </div>
                                            </div>
                                        </a>
                                    </Link>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Layout>
    )
}
